import { useRef, useState } from "react";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Upload, Loader2, Link as LinkIcon, X } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Given a stored value (either a full https URL or a "/api/files/..." relative path),
// return an absolute URL the <img> tag can load.
export function resolveImageUrl(value) {
  if (!value) return "";
  if (value.startsWith("http://") || value.startsWith("https://")) return value;
  if (value.startsWith("/api/")) return `${BACKEND_URL}${value}`;
  if (value.startsWith("culinaire/")) return `${BACKEND_URL}/api/files/${value}`;
  return value;
}

export default function ImageUpload({ value, onChange, testId = "image-upload", label = "Recipe image" }) {
  const [uploading, setUploading] = useState(false);
  const [showUrl, setShowUrl] = useState(false);
  const inputRef = useRef(null);

  const pick = () => inputRef.current?.click();

  const upload = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 12 * 1024 * 1024) {
      toast.error("File too large (max 12 MB)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/files/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url); // /api/files/<path>
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
    }
  };

  const src = resolveImageUrl(value);

  return (
    <div className="space-y-2" data-testid={testId}>
      <div className="flex items-center justify-between">
        <span className="label-eyebrow">{label}</span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setShowUrl((v) => !v)}
            className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
            data-testid={`${testId}-toggle-url`}
          >
            <LinkIcon className="w-3 h-3" /> {showUrl ? "hide URL" : "paste URL"}
          </button>
        </div>
      </div>

      {src ? (
        <div className="relative rounded-lg overflow-hidden border border-border group">
          <img src={src} alt="preview" className="w-full h-40 object-cover" data-testid={`${testId}-preview`} />
          <button
            type="button"
            onClick={() => onChange("")}
            className="absolute top-2 right-2 w-7 h-7 rounded-full bg-card/95 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
            data-testid={`${testId}-remove`}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={pick}
          className="w-full border border-dashed border-border rounded-lg py-10 text-center text-sm text-muted-foreground hover:border-foreground/40 hover:bg-secondary/40 transition-colors"
          data-testid={`${testId}-dropzone`}
        >
          {uploading ? (
            <span className="inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</span>
          ) : (
            <span className="inline-flex items-center gap-2"><Upload className="w-4 h-4" /> Click to upload • JPG • PNG • WEBP</span>
          )}
        </button>
      )}

      <div className="flex items-center gap-2">
        <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid={`${testId}-file-input`} />
        {src && (
          <Button type="button" size="sm" variant="outline" onClick={pick} disabled={uploading} data-testid={`${testId}-replace`}>
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            <span className="ml-1">Replace</span>
          </Button>
        )}
        {showUrl && (
          <Input
            placeholder="…or paste image URL"
            value={value?.startsWith("http") ? value : ""}
            onChange={(e) => onChange(e.target.value)}
            className="text-xs"
            data-testid={`${testId}-url-input`}
          />
        )}
      </div>
    </div>
  );
}
